import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    FaFolderOpen, FaPlus, FaPen, FaLayerGroup, FaLock, FaStar, FaRegStar,
    FaArchive, FaUndo, FaSlidersH, FaChartBar,
} from "react-icons/fa";
import { PROJECTS_CONSTANTS } from "../_MODULES/_API";
import { ProjectFormModal } from "../_UI/ProjectFormModal";
import { COMPANIES_CONSTANTS } from "../../_Companies/_MODULES/_API";
import { STATUS_MAP_PROJECT, OPTIONS_PROJECT_STATUS } from "../../../_Catalogs.Views";
import { PageHeader } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { StatusBadge } from "../../../../../_CORE/APP/Templates/_UI/Status/_index";
import { Card, EmptyState, Skeleton, Chip, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { FieldDate, FieldInput, FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { onApiAction } from "../../../../_UI/onApiAction";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { AdvancedFilterPanel, onDateMatches } from "../../../../_UI/AdvancedFilterPanel";
import { usePagedList } from "../../../../_UI/usePagedList";
import { StatisticsModal } from "../../../../_UI/StatisticsModal";
import "../_UI/_Styles/Projects.css";

const PROJECT_SCOPE_OPTIONS = [
    { value: "ACTIVE", label: "Proyectos activos" },
    { value: "ARCHIVED", label: "Proyectos archivados" },
];

export const ProjectsViewMain = () => {

    const [useSearch, setSearch] = useState("");
    const [useStatus, setStatus] = useState("");
    const [useCompanyId, setCompanyId] = useState("");
    const [useProjectScope, setProjectScope] = useState("ACTIVE");
    const [statisticsOpen, setStatisticsOpen] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advanced, setAdvanced] = useState({ favorite: "ALL", pin: "ALL", stack: "", subproject: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });
    const [useProjectEdit, setProjectEdit] = useState(null);
    const [useIsModalOpen, setIsModalOpen] = useState(false);
    const [useActiveProjects, setActiveProjects] = useState([]);
    const [useArchivedProjects, setArchivedProjects] = useState([]);

    const { apiGet: onGetProjects, useApiData: PROJECTS, useApiLoading: IS_LOADING } = useApiQuery({
        minTimeQuery: 300, enablePagination: false, startLoading: true,
    });

    const { apiGet: onGetArchivedProjects, useApiData: ARCHIVED_PROJECTS, useApiLoading: IS_ARCHIVED_LOADING } = useApiQuery({
        minTimeQuery: 250, enablePagination: false,
    });

    const { apiGet: onGetCompanies, useApiData: COMPANIES } = useApiQuery({
        minTimeQuery: 300, enablePagination: false,
    });


    useEffect(() => {
        onGetCompanies({ endpoint: COMPANIES_CONSTANTS.LIST });
        onGetProjects({ endpoint: PROJECTS_CONSTANTS.LIST });
        onGetArchivedProjects({ endpoint: PROJECTS_CONSTANTS.ARCHIVE_LIST });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { setActiveProjects(PROJECTS ?? []); }, [PROJECTS]);
    useEffect(() => { setArchivedProjects(ARCHIVED_PROJECTS ?? []); }, [ARCHIVED_PROJECTS]);

    const COMPANY_OPTIONS = [
        { value: "", label: "Todas las compañías" },
        ...(COMPANIES ?? []).map((company) => ({ value: company.id, label: company.name })),
    ];

    const STATUS_OPTIONS = [{ value: "", label: "Todos los estados" }, ...OPTIONS_PROJECT_STATUS];

    const SOURCE_PROJECTS = useProjectScope === "ARCHIVED" ? useArchivedProjects : useActiveProjects;

    const FILTERED_PROJECTS = useMemo(() => SOURCE_PROJECTS
        .filter((project) => !useCompanyId || project.companyId === useCompanyId || project.company?.id === useCompanyId)
        .filter((project) => !useStatus || project.status === useStatus)
        .filter((project) => advanced.favorite === "ALL" || (!!project.isFavorite === (advanced.favorite === "YES")))
        .filter((project) => advanced.pin === "ALL" || (!!project.hasAccessPin === (advanced.pin === "YES")))
        .filter((project) => !String(advanced.stack || "").trim() || onMatchesSearch(advanced.stack, project.stack || []))
        .filter((project) => !String(advanced.subproject || "").trim() || onMatchesSearch(advanced.subproject, (project.subProjects || []).flatMap((sub) => [sub.name, sub.description, sub.type, ...(sub.stack || [])])))
        .filter((project) => onDateMatches(advanced.dateField === "CREATED" ? project.createdAt : project.updatedAt, advanced.dateRange, advanced.dateExact))
        .filter((project) => {
            const term = useSearch.trim().toLowerCase();
            if (!term) return true;
            const haystack = [
                project.name, project.description, project.company?.name, project.repositoryUrl,
                ...(project.stack ?? []),
                ...(project.subProjects ?? []).flatMap((subProject) => [
                    subProject.name, subProject.type, subProject.description, ...(subProject.stack ?? []),
                ]),
            ].filter(Boolean).join(" ").toLowerCase();
            return haystack.includes(term);
        }), [SOURCE_PROJECTS, useCompanyId, useStatus, useSearch, advanced]);

    const advancedCount = [useCompanyId, useStatus, advanced.favorite !== "ALL", advanced.pin !== "ALL", String(advanced.stack || "").trim(), String(advanced.subproject || "").trim(), advanced.dateRange?.[0] || advanced.dateRange?.[1], advanced.dateExact].filter(Boolean).length;
    const clearAdvanced = () => {
        setCompanyId("");
        setStatus("");
        setAdvanced({ favorite: "ALL", pin: "ALL", stack: "", subproject: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });
    };

    const SORTED_PROJECTS = [...FILTERED_PROJECTS].sort((a, b) =>
        (Number(!!b.isFavorite) - Number(!!a.isFavorite)) || String(b.updatedAt ?? "").localeCompare(String(a.updatedAt ?? ""))
    );

    const PAGER = usePagedList(SORTED_PROJECTS, 6, `projects-${useProjectScope}`);

    const onOpenCreate = () => {
        setProjectEdit(null);
        setIsModalOpen(true);
    };

    const onOpenEdit = (project) => {
        setProjectEdit(project);
        setIsModalOpen(true);
    };

    const onPatchProjectLocal = (id, patch) => {
        setActiveProjects((rows) => rows.map((row) => row.id === id ? { ...row, ...patch } : row));
        setArchivedProjects((rows) => rows.map((row) => row.id === id ? { ...row, ...patch } : row));
    };

    const onToggleFavorite = async (project) => {
        const previous = !!project.isFavorite;
        const next = !previous;
        onPatchProjectLocal(project.id, { isFavorite: next });

        const RESPONSE = await onSilentPatch({
            endpoint: PROJECTS_CONSTANTS.UPDATE,
            id: project.id,
            data: { isFavorite: next },
            errorMessage: "No se pudo guardar el destacado del proyecto.",
        });

        if (!RESPONSE.success) onPatchProjectLocal(project.id, { isFavorite: previous });
    };

    const onArchive = async (project) => {
        const IS_CONFIRMED = await confirm.danger(
            "Archivar proyecto",
            `Se archivará “${project.name}” con sus subproyectos, bitácora y credenciales. Lo podés restaurar desde Proyectos archivados.`,
        );
        if (!IS_CONFIRMED) return;

        setActiveProjects((rows) => rows.filter((row) => row.id !== project.id));
        setArchivedProjects((rows) => [{ ...project, archivedAt: new Date().toISOString() }, ...rows]);

        const RESPONSE = await onApiAction({
            method: "PATCH", endpoint: PROJECTS_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: project.id }, successMessage: "Proyecto archivado",
        });

        if (!RESPONSE.success) {
            setArchivedProjects((rows) => rows.filter((row) => row.id !== project.id));
            setActiveProjects((rows) => [project, ...rows]);
        }
    };

    const onRestore = async (project) => {
        setArchivedProjects((rows) => rows.filter((row) => row.id !== project.id));
        setActiveProjects((rows) => [{ ...project, archivedAt: null }, ...rows]);

        const RESPONSE = await onApiAction({
            method: "PATCH", endpoint: PROJECTS_CONSTANTS.RESTORE_ONE,
            pathParams: { id: project.id }, successMessage: "Proyecto restaurado",
        });

        if (!RESPONSE.success) {
            setActiveProjects((rows) => rows.filter((row) => row.id !== project.id));
            setArchivedProjects((rows) => [project, ...rows]);
        }
    };

    const IS_SCOPE_LOADING = useProjectScope === "ARCHIVED" ? IS_ARCHIVED_LOADING : IS_LOADING;

    return (
        <ViewLayout
            header={<PageHeader
                title={useProjectScope === "ARCHIVED" ? "Proyectos archivados" : "Proyectos"}
                description={useProjectScope === "ARCHIVED"
                    ? "Restaurá un proyecto desde acá. La eliminación definitiva queda centralizada en Archivados."
                    : "Todo lo que estás trabajando, agrupado por compañía."}
                icon={<FaFolderOpen />}
                actions={<>
                    <AppButton variant="outline" tone="neutral" icon={<FaChartBar />} isIconOnly title="Estadísticas" aria-label="Estadísticas" onClick={() => setStatisticsOpen(true)} />
                    {useProjectScope === "ACTIVE" && <AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nuevo proyecto</AppButton>}
                </>} />}
            toolbar={<ViewToolbar
                filters={<>
                    <SearchFilter value={useSearch} onChange={setSearch} total={SOURCE_PROJECTS.length} matches={FILTERED_PROJECTS.length}
                        placeholder="Proyecto, subproyecto, stack o compañía" />
                    <FieldSelect id="projectScope" label={null} options={PROJECT_SCOPE_OPTIONS}
                        value={useProjectScope} onChange={(event) => setProjectScope(event.target.value)} />
                    <AppButton variant={advancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />} onClick={() => setAdvancedOpen((current) => !current)}>{advancedCount ? `Avanzado (${advancedCount})` : "Avanzado"}</AppButton>
                </>}
                expandedContent={<AdvancedFilterPanel isOpen={advancedOpen} activeCount={advancedCount} onClear={clearAdvanced}>
                    <FieldSelect id="companyId" label="Compañía" options={COMPANY_OPTIONS} isSearchable
                        value={useCompanyId} onChange={(event) => setCompanyId(event.target.value)} />
                    <FieldSelect id="status" label="Estado" options={STATUS_OPTIONS} isSearchable
                        value={useStatus} onChange={(event) => setStatus(event.target.value)} />
                    <FieldSelect id="projects_adv_favorite" label="Destacado" value={advanced.favorite} onChange={(event) => setAdvanced((current) => ({ ...current, favorite: event.target.value }))} options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Destacados" }, { value: "NO", label: "No destacados" }]} />
                    <FieldSelect id="projects_adv_pin" label="PIN" value={advanced.pin} onChange={(event) => setAdvanced((current) => ({ ...current, pin: event.target.value }))} options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Con PIN" }, { value: "NO", label: "Sin PIN" }]} />
                    <FieldInput id="projects_adv_stack" label="Stack" value={advanced.stack} onChange={(event) => setAdvanced((current) => ({ ...current, stack: event.target.value }))} />
                    <FieldInput id="projects_adv_sub" label="Subproyecto" value={advanced.subproject} onChange={(event) => setAdvanced((current) => ({ ...current, subproject: event.target.value }))} />
                    <FieldSelect id="projects_adv_date" label="Fecha" value={advanced.dateField} onChange={(event) => setAdvanced((current) => ({ ...current, dateField: event.target.value }))} options={[{ value: "UPDATED", label: "Última actualización" }, { value: "CREATED", label: "Creación" }]} />
                    <FieldDate id="projects_adv_from" label="Desde" value={advanced.dateRange?.[0] || null} onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [date || null, current.dateRange?.[1] || null], dateExact: null }))} isClearable />
                    <FieldDate id="projects_adv_to" label="Hasta" value={advanced.dateRange?.[1] || null} onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [current.dateRange?.[0] || null, date || null], dateExact: null }))} isClearable />
                    <FieldDate id="projects_adv_exact" label="Fecha exacta" value={advanced.dateExact} onChange={(date) => setAdvanced((current) => ({ ...current, dateExact: date || null, dateRange: date ? [null, null] : current.dateRange }))} isClearable />
                </AdvancedFilterPanel>}
                total={PAGER.total}
                perPage={PAGER.usePerPage}
                onChangePerPage={PAGER.onChangePerPage}
                exportColumns={[
                    { key: "name", label: "Proyecto" },
                    { key: "company", label: "Compañía" },
                    { key: "status", label: "Estado" },
                    { key: "stack", label: "Stack" },
                    { key: "subProjects", label: "Subproyectos" },
                ]}
                exportRows={SORTED_PROJECTS.map((project) => ({
                    name: project.name,
                    company: project.company?.name ?? "",
                    status: project.status,
                    stack: (project.stack ?? []).join(", "),
                    subProjects: project.subProjects?.length ?? 0,
                }))}
                exportName={useProjectScope === "ARCHIVED" ? "proyectos-archivados" : "proyectos"} /> }>

            {IS_SCOPE_LOADING && (
                <div className="LStudio__app_projects_grid">
                    <Skeleton variant="box" height={170} />
                    <Skeleton variant="box" height={170} />
                    <Skeleton variant="box" height={170} />
                </div>
            )}

            {!IS_SCOPE_LOADING && SORTED_PROJECTS.length === 0 && (
                <EmptyState icon={useProjectScope === "ARCHIVED" ? <FaArchive /> : <FaFolderOpen />}
                    title={useProjectScope === "ARCHIVED" ? "No hay proyectos archivados" : "No hay proyectos para mostrar"}
                    description={useProjectScope === "ARCHIVED"
                        ? "Los proyectos que archives van a aparecer acá."
                        : "Creá uno nuevo o limpiá los filtros."}
                    action={useProjectScope === "ACTIVE"
                        ? <AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nuevo proyecto</AppButton>
                        : null} />
            )}

            {!IS_SCOPE_LOADING && SORTED_PROJECTS.length > 0 && (
                <div className="LStudio__app_projects_grid">
                    {PAGER.paged.map((project) => (
                        <Card key={project.id} className="LStudio__app_projects_card" data-favorite={!!project.isFavorite}>
                            <div className="LStudio__app_projects_card_head">
                                <span className="LStudio__app_projects_card_dot"
                                    style={{ background: project.color || "var(--LS__accent, #e83a52)" }} />

                                {useProjectScope === "ACTIVE" ? (
                                    <Link to={`/projects/${project.id}/subprojects`} className="LStudio__app_projects_card_name">
                                        {project.isFavorite && <FaStar className="LStudio__app_record_star" title="Destacado" />}
                                        {project.name}
                                    </Link>
                                ) : (
                                    <span className="LStudio__app_projects_card_name">
                                        {project.isFavorite && <FaStar className="LStudio__app_record_star" title="Destacado" />}
                                        {project.name}
                                    </span>
                                )}

                                <StatusBadge status={project.status} map={STATUS_MAP_PROJECT} size="sm" />
                                {useProjectScope === "ARCHIVED" && <Badge tone="neutral" size="sm">Archivado</Badge>}

                                <CardActionMenu title="Acciones del proyecto" items={useProjectScope === "ARCHIVED" ? [
                                    { key: "favorite", icon: project.isFavorite ? <FaStar /> : <FaRegStar />, label: project.isFavorite ? "Quitar de destacados" : "Destacar", onClick: () => onToggleFavorite(project) },
                                    { key: "edit", icon: <FaPen />, label: "Editar", onClick: () => onOpenEdit(project) },
                                    { key: "restore", icon: <FaUndo />, label: "Restaurar", onClick: () => onRestore(project) },
                                    { divider: true },
                                ] : [
                                    { key: "favorite", icon: project.isFavorite ? <FaStar /> : <FaRegStar />, label: project.isFavorite ? "Quitar de destacados" : "Destacar", onClick: () => onToggleFavorite(project) },
                                    { key: "edit", icon: <FaPen />, label: "Editar", onClick: () => onOpenEdit(project) },
                                    { divider: true },
                                    { key: "archive", icon: <FaArchive />, label: "Archivar", tone: "danger", onClick: () => onArchive(project) },
                                ]} />
                            </div>

                            <p className="LStudio__app_projects_card_company">
                                {project.company?.name ?? "Sin compañía"}
                            </p>

                            {project.description && <p className="LStudio__app_projects_card_desc">{project.description}</p>}

                            {project.stack?.length > 0 && (
                                <div className="LStudio__app_projects_card_stack">
                                    {project.stack.map((tech) => <Chip key={tech} tone="neutral">{tech}</Chip>)}
                                </div>
                            )}

                            <div className="LStudio__app_projects_card_path">
                                <span><FaLayerGroup /> {project.subProjects?.length ?? 0} subproyectos</span>
                                {project.hasAccessPin && <span><FaLock /> protegido con pin</span>}
                            </div>

                            {useProjectScope === "ACTIVE" && <div className="LStudio__app_projects_card_actions">
                                <AppButton size="sm" variant="soft" to={`/projects/${project.id}/subprojects`}>Abrir</AppButton>
                            </div>}
                        </Card>
                    ))}
                </div>
            )}

            <ViewPagination page={PAGER.usePage} totalPages={PAGER.totalPages}
                total={PAGER.total} perPage={PAGER.usePerPage} onChange={PAGER.onChangePage} />

            <StatisticsModal isOpen={statisticsOpen} onClose={() => setStatisticsOpen(false)}
                title="Estadísticas · Proyectos" description="Resumen general de los proyectos guardados."
                metrics={[
                    { key: "active", label: "Activos", value: useActiveProjects.length, icon: <FaFolderOpen />, tone: "accent", isLoading: IS_LOADING },
                    { key: "archived", label: "Archivados", value: useArchivedProjects.length, icon: <FaArchive />, tone: "neutral", isLoading: IS_ARCHIVED_LOADING },
                    { key: "subprojects", label: "Subproyectos", value: useActiveProjects.reduce((total, project) => total + (project.subProjects?.length ?? 0), 0), icon: <FaLayerGroup />, tone: "neutral" },
                    { key: "protected", label: "Con PIN", value: useActiveProjects.filter((project) => project.hasAccessPin).length, icon: <FaLock />, tone: "warn" },
                    { key: "favorite", label: "Destacados", value: useActiveProjects.filter((project) => project.isFavorite).length, icon: <FaStar />, tone: "neutral" },
                ]} />

            <ProjectFormModal
                isOpen={useIsModalOpen}
                project={useProjectEdit}
                projects={[...useActiveProjects, ...useArchivedProjects]}
                companies={COMPANIES ?? []}
                onClose={() => setIsModalOpen(false)}
                onSaved={(saved, isEdit) => {
                    const current = [...useActiveProjects, ...useArchivedProjects].find((row) => row.id === saved.id);
                    const normalized = {
                        ...current,
                        ...saved,
                        company: (COMPANIES ?? []).find((company) => company.id === saved.companyId) ?? saved.company ?? current?.company ?? null,
                        subProjects: saved.subProjects ?? current?.subProjects ?? [],
                    };
                    if (isEdit) {
                        onPatchProjectLocal(saved.id, normalized);
                        return;
                    }
                    setActiveProjects((rows) => [normalized, ...rows]);
                }} />

        </ViewLayout>
    );
};
